import { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";
import ShareOption from "./ShareOption";
import { CommunityPost } from "../post/interfaces/post.interface";
import { useShareActions } from "../../../../hooks/useShareActions";

type Props = {
  post?: CommunityPost;
  open: boolean;
  onClose: () => void;
};

const ShareModal: React.FC<Props> = ({ post, open, onClose }) => {
  const {
    copied,
    shareUrl,
    handleCopyLink,
    handleNativeShare,
    handleShareToFeed,
    handleShareToStory,
    handleShareToMessenger,
    handleShareToWhatsApp,
    handleShareToTelegram,
  } = useShareActions({ post, onClose });

  // ✅ Escape key (Dialog بيعملها تلقائياً بس نتأكد)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, onClose]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        aria-labelledby="share-modal-title"
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel
              className="
                relative w-full sm:max-w-md
                bg-white dark:bg-deepSlate
                rounded-t-2xl sm:rounded-2xl
                shadow-xl
                overflow-hidden
                max-h-[90vh] flex flex-col
              "
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b dark:border-gray-700 shrink-0">
                <Dialog.Title
                  id="share-modal-title"
                  className="font-semibold text-lg dark:text-white"
                >
                  Share Post
                </Dialog.Title>

                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close share dialog"
                  className="
                    p-2 rounded-full cursor-pointer
                    hover:bg-gray-100 dark:hover:bg-gray-800
                    transition
                    focus:outline-none focus:ring-2 focus:ring-primary-500
                  "
                >
                  <Icon icon="mdi:close" className="w-5 h-5" />
                </button>
              </div>

              {/* Options — scrollable */}
              <div className="divide-y dark:divide-gray-700 overflow-y-auto">
                {/* Native Share (mobile only) */}
                {typeof navigator !== "undefined" &&
                  "share" in navigator && (
                    <ShareOption
                      icon="mdi:share-variant"
                      label="Share via..."
                      onClick={handleNativeShare}
                      highlighted
                    />
                  )}

                <ShareOption
                  icon="mdi:newspaper-variant-outline"
                  label="Share to Feed"
                  onClick={handleShareToFeed}
                />

                <ShareOption
                  icon="mdi:camera-outline"
                  label="Share to Story"
                  onClick={handleShareToStory}
                />

                <ShareOption
                  icon="mdi:facebook-messenger"
                  label="Send in Messenger"
                  onClick={handleShareToMessenger}
                />

                <ShareOption
                  icon="mdi:whatsapp"
                  label="Share on WhatsApp"
                  onClick={handleShareToWhatsApp}
                />

                <ShareOption
                  icon="mdi:telegram"
                  label="Share on Telegram"
                  onClick={handleShareToTelegram}
                />

                <ShareOption
                  icon={copied ? "mdi:check-circle" : "mdi:link-variant"}
                  label={copied ? "Link Copied!" : "Copy Link"}
                  onClick={handleCopyLink}
                  highlighted={copied}
                />
              </div>

              {/* Footer: URL preview */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-700 shrink-0">
                <div className="flex items-center gap-2">
                  <Icon
                    icon="mdi:link"
                    className="w-4 h-4 text-gray-400 shrink-0"
                  />
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    className="
                      flex-1 text-xs bg-transparent
                      text-gray-600 dark:text-gray-400
                      outline-none truncate
                    "
                  />
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ShareModal;